import { isActionTargetShape } from './typia-gen/action-target'
import { DEEPLINK_PATTERN, parseDeeplink } from './deeplink'
import type { DeeplinkElementId } from '../view/pagecontent/menu-types'
import type { ActionTarget, EventId, FestivalMapId, GalleryItemId, GroupId, GuestTypeId, InfoPratiquesId, ProgramId } from '../grab/types'

/**
 * Checks whether an unknown value is a structurally valid {@link ActionTarget}
 * at runtime.
 *
 * Validates the shape of the discriminated union so callers can safely access
 * `url` or `element` without type-narrowing lies. The underlying typia guard
 * validates the brand-stripped shape (see {@link isActionTargetShape}); the
 * nominal `__chapito` brands are erased at runtime, so a value matching that
 * shape is a valid `ActionTarget`.
 */
export function isValidActionTarget(value: unknown): value is ActionTarget {
  return isActionTargetShape(value)
}

const EMPTY_ELEMENT_ID = '' as DeeplinkElementId

export function legacyUrlToActionTarget(url: string): ActionTarget {
  const match = DEEPLINK_PATTERN.exec(url)
  if (!match) return { type: 'EXTERNAL_URL', url }

  const [, scheme] = match
  if (scheme === 'http' || scheme === 'https' || scheme === 'mailto' || scheme === 'tel') {
    return { type: 'EXTERNAL_URL', url }
  }

  const parsed = parseDeeplink(url)
  const param = parsed.paramsAsMap['id'] ?? ''

  switch (parsed.target) {
    case 'home':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'HOME' } }
    case 'showMan':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'GUEST', groupId: param as GroupId } }
    case 'show':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'EVENT', eventId: param as EventId } }
    case 'program':
      return {
        type: 'DEEPLINK',
        element: { id: EMPTY_ELEMENT_ID, type: 'PROGRAM', programIds: param.split(',') as ProgramId[], defaultSegment: undefined },
      }
    case 'sponsors':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'SPONSOR_LIST' } }
    case 'practicalInfo':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'PRACTICAL_INFO', infoPratiqueId: param as InfoPratiquesId } }
    case 'map':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'MAP', mapIds: param.split(',') as FestivalMapId[] } }
    case 'notifications':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'NOTIFICATIONS' } }
    case 'webview':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'WEBVIEW', url: decodeURIComponent(param), openInWebview: true } }
    case 'extern':
      return {
        type: 'DEEPLINK',
        element: { id: EMPTY_ELEMENT_ID, type: 'WEBVIEW', url: decodeURIComponent(param), openInWebview: false },
      }
    case 'gallery':
      return {
        type: 'DEEPLINK',
        element: {
          id: EMPTY_ELEMENT_ID,
          type: 'GALLERY',
          galleryData: param
            .split(',')
            .filter(Boolean)
            .map((id) => ({ id: id as GalleryItemId, title: '', weight: 0, thumbnailImage: '' as any, image: '' as any })),
        },
      }
    case 'showmen':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'GUEST_LIST_TYPE', typeIds: param.split(',') as GuestTypeId[] } }
    case 'practicalInfos':
      return {
        type: 'DEEPLINK',
        element: { id: EMPTY_ELEMENT_ID, type: 'PRACTICAL_INFO_LIST', infoPratiqueIds: param.split(',') as InfoPratiquesId[] },
      }
    case 'chat':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'CHAT' } }
    case 'weezevent':
      return { type: 'DEEPLINK', element: { id: EMPTY_ELEMENT_ID, type: 'WEEZEVENT', url: decodeURIComponent(param) } }
    default:
      return { type: 'EXTERNAL_URL', url }
  }
}

/**
 * Parses an unknown value into a validated {@link ActionTarget}.
 *
 * Accepts an already-parsed object or a JSON string. Returns a fallback `ActionTarget`
 * when the input is a legacy string. Returns `null` if completely invalid.
 */
export function resolveActionTarget(target: string | ActionTarget): ActionTarget | null {
  let value: unknown

  if (typeof target === 'string') {
    try {
      value = JSON.parse(target)
    } catch {
      return legacyUrlToActionTarget(target)
    }
  } else {
    value = target
  }

  return isValidActionTarget(value) ? value : null
}

/**
 * Extracts a browser-openable URL from an ActionTarget.
 *
 * Returns a URL only for:
 * - `EXTERNAL_URL` targets
 * - `DEEPLINK` targets of type `TICKETING` or `CASHLESS` (which embed an external URL)
 *
 * Returns `undefined` for all other in-app deeplinks.
 */
export function getActionTargetUrl(target: ActionTarget): string | undefined {
  if (target.type === 'EXTERNAL_URL') {
    return target.url || undefined
  }

  if (target.element.type === 'TICKETING' || target.element.type === 'CASHLESS') {
    return target.element.url
  }

  return undefined
}
